import Alert from '@/_application/core/components/ui/alert';
import {Button} from '@kidslabs/mario-ui-nextjs-react-core';
import {Form} from '@/_application/core/components/ui/forms/form';
import Input from '@/_application/core/components/ui/forms/input';
import PasswordInput from '@/_application/core/components/ui/forms/password-input';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import type {LoginUserInput} from '@/_application/core/types';
import {AnonymousIcon} from '@/_application/icons/anonymous-icon';
import {MobileIcon} from '@/_application/icons/mobile-icon';
import {CheckoutStep} from '@/lib/constants';
import {authorizationAtom} from '@/store/authorization-atom';
import {currentCheckoutStepAtom} from '@/store/cart';
import {useAtom} from 'jotai';
import {getSession, signIn} from 'next-auth/react';
import {useTranslation} from 'next-i18next';
import {useRouter} from 'next/router';
import {useRouter as useNavigationRouter} from 'next/navigation';
import {useEffect, useState} from 'react';
import * as yup from 'yup';
import {WebStorage} from '@/_application/core/storage/web-storage';
import {AuthEnum} from '@/_application/modules/auth/enum/auth';
import {Customer} from '@/__generated__/__types__';
import useEventTrackingService from '@/_application/modules/tracking/service/event-tracking.service';
import {CustomerDataInput, TrackingTargetProviderEnum} from '@kidslabs/mario-client-event-tracking';
import {getFullName} from '@/_application/core/utils/common';
import {getDeviceInformation} from '@/_application/core/utils/device';

const loginFormSchema = yup.object().shape({
    email: yup
        .string()
        .required('error-email-required')
        .test('email-Or-Phone-validation', 'error-format-email-or-phone', function (value) {
            const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
            const phoneRegex = /^(84|0[3|5|7|8|9])+([0-9]{8})\b/g;
            const isValid = value ? emailRegex.test(value) || phoneRegex.test(value) : false;
            return isValid;
        }),
    password: yup.string().required('error-password-required'),
});

export function LoginForm() {
    const {t} = useTranslation('common');
    const router = useRouter();
    const navigationRouter = useNavigationRouter();
    const {openModal, closeModal} = useModalAction();
    const isCheckout = router.pathname.includes('checkout');
    const [isAuthorized, setAuthorized] = useAtom(authorizationAtom);
    const [, setCurrentCheckoutStep] = useAtom(currentCheckoutStepAtom);
    const [serverError, setServerError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const {trackSignInEvent: cdpTrackSignInEvent, trackIdentifyUserEvent: cdpTrackIdentifyUserEvent} =
        useEventTrackingService(TrackingTargetProviderEnum.CDP);

    useEffect(() => {
        if (isAuthorized) {
            setCurrentCheckoutStep(CheckoutStep.shippingAddress);
        }
    }, [isAuthorized]);

    const trackSignIn = (customer: Customer) => {
        if (!customer) return;
        const customerInput: CustomerDataInput = {
            id: customer?.phone_number,
            name: getFullName(customer),
            email: customer?.email as string,
            phone: customer?.phone_number,
        };

        cdpTrackSignInEvent({customer: customerInput}).then(() => {});
        cdpTrackIdentifyUserEvent({identifyType: 'User', customer: customerInput}).then(() => {});
    };

    async function onSubmit({email, password}: LoginUserInput) {
        setIsLoading(true);
        setServerError('');
        try {
            const device = getDeviceInformation();
            const results: any = await signIn('username_and_password', {
                email,
                password,
                redirect: false,
                device: JSON.stringify(device),
            });
            if (results?.error) {
                if (results.status === 401) {
                    setServerError(
                        'Đăng nhập không thành công. Vui lòng kiểm tra lại thông tin đăng nhập của bạn và thử lại.',
                    );
                }
            } else {
                const session = (await getSession()) as any;
                if (session) {
                    WebStorage.setItem(AuthEnum.ACCESS_TOKEN, session?.token?.access_token);
                    setAuthorized(true);
                    const customer = session?.user as Customer;
                    trackSignIn(customer);
                    navigationRouter.refresh();
                    closeModal();
                } else {
                    setServerError('Invalid credentials');
                }
            }
        } catch (error: any) {
            setServerError(error.message);
        }

        setIsLoading(false);
    }

    const onGuestCheckoutHandler = () => {
        setCurrentCheckoutStep(CheckoutStep.shippingAddress);
    };

    return (
        <>
            <Alert
                variant="error"
                message={serverError && t(serverError)}
                className="mb-6"
                closeable={true}
                onClose={() => setServerError('')}
            />
            <Form<LoginUserInput> onSubmit={onSubmit} validationSchema={loginFormSchema}>
                {({register, formState: {errors}}) => (
                    <>
                        <Input
                            label={t('text-email-phone')}
                            {...register('email')}
                            type="email"
                            variant="outline"
                            className="mb-5"
                            required={true}
                            dimension="medium"
                            placeholder={t('text-type-email-or-phone')}
                            error={t(errors.email?.message!)}
                            aria-label="Email hoặc số điện thoại"
                        />
                        <PasswordInput
                            label={t('text-password')}
                            {...register('password')}
                            error={t(errors.password?.message!)}
                            variant="outline"
                            className="mb-5"
                            dimension="medium"
                            required={true}
                            placeholder={t('text-type-password')}
                            forgotPageRouteOnClick={() => openModal('FORGOT_VIEW')}
                            registerPageRouteOnClick={() => openModal('REGISTER')}
                        />
                        <div className="mt-8">
                            <Button
                                aria-label="Đăng nhập"
                                className="mt-2 w-full bg-blue-kid hover:bg-primary"
                                loading={isLoading}
                                disabled={isLoading}>
                                {t('text-login', 'Đăng nhập')}
                            </Button>
                        </div>
                    </>
                )}
            </Form>
            {/* //===============// */}
            <div className="relative mb-6 mt-8 flex flex-col items-center justify-center text-sm text-heading sm:mb-8 sm:mt-11">
                <hr className="w-full" />
                <span className="absolute -top-2.5 bg-primary-0 px-2 ltr:left-2/4 ltr:-ml-4 rtl:right-2/4 rtl:-mr-4">
                    {t('text-or')}
                </span>
            </div>
            <div className="mt-2 grid grid-cols-1 gap-4">
                {/* <Button
          className="bg-social-google! !text-white hover:bg-social-google-hover!"
          disabled={isLoading}
          onClick={() => {
            window.location.href = `/social/account/login/provider/google/`;
          }}
        >
          <GoogleIcon className="h-4 w-4 ltr:mr-3 rtl:ml-3" />
          {t('text-login-google')}
        </Button> */}

                <Button
                    className="h-11 w-full bg-gray-500! !text-white hover:bg-gray-600! sm:h-12"
                    aria-label="Đăng nhập bằng số điện thoại"
                    disabled={isLoading}
                    onClick={() => openModal('OTP_LOGIN')}>
                    <MobileIcon className="h-5 text-white ltr:mr-2 rtl:ml-2" />
                    {t('text-login-mobile')}
                </Button>

                {isCheckout && (
                    <Button
                        aria-label="Thanh toán không cần đăng nhập"
                        className="h-11 w-full bg-pink-700! !text-white hover:bg-pink-800! sm:h-12"
                        disabled={isLoading}
                        onClick={onGuestCheckoutHandler}>
                        <AnonymousIcon className="h-6 text-white ltr:mr-2 rtl:ml-2" />
                        {t('text-guest-checkout')}
                    </Button>
                )}
            </div>
        </>
    );
}

export default function LoginView() {
    const {t} = useTranslation('common');
    return (
        <div className="flex h-full min-h-screen w-screen flex-col justify-center bg-primary-0 px-5 py-6 sm:p-8 md:h-auto md:min-h-0 md:max-w-[480px] md:rounded-xl">
            <p className="mb-8 mt-4 text-center text-sm text-body sm:mb-10 sm:mt-5 md:text-base">{t('login-helper')}</p>
            <LoginForm />
        </div>
    );
}
